import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
} from 'reactstrap';
import moment from 'moment';
import { getAuth } from 'firebase/auth';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import {
  subscribeLicenseClaimNotifications,
  dismissLicenseClaimNotification,
  getNotificationPreferences,
  LicenseClaimNotification,
} from '../../services/notification.service';

interface LicenseClaimedModalProps {
  firebase: any;
}

const formatLicenseType = (type: string) => {
  switch (type) {
    case 'blanket': return 'Blanket License';
    case '1year':   return '1 Year';
    case '2year':   return '2 Years';
    case '3year':   return '3 Years';
    case 'custom':  return 'Custom Duration';
    default:        return type;
  }
};

const LicenseClaimedModal: React.FC<LicenseClaimedModalProps> = ({ firebase }) => {
  const [notifications, setNotifications] = useState<LicenseClaimNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [dismissingAll, setDismissingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  const isOpen = useNotificationsStore((s) => s.showLicenseClaimedModal);
  const setIsOpen = useNotificationsStore((s) => s.setShowLicenseClaimedModal);
  const setLicenseClaimedCount = useNotificationsStore((s) => s.setLicenseClaimedCount);

  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setNotifications([]);
      setLicenseClaimedCount(0);
      return;
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
      if (cancelled) return;
      if (!prefs.inApp.licenseClaimed) {
        setNotifications([]);
        setLicenseClaimedCount(0);
        setLoading(false);
        return;
      }

      let autoOpenedThisOrg = false;
      unsubscribe = subscribeLicenseClaimNotifications(firebase, currentOrgId, (incoming) => {
        setNotifications(incoming);
        setLicenseClaimedCount(incoming.length);
        setLoading(false);

        if (!autoOpenedThisOrg && !hasAutoOpened && incoming.length > 0) {
          autoOpenedThisOrg = true;
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentOrgId, firebase, hasAutoOpened, setLicenseClaimedCount, setIsOpen]);

  const handleDismiss = async (notification: LicenseClaimNotification) => {
    if (!currentOrgId || !firebase) return;
    setDismissingIds((prev) => new Set(prev).add(notification.id));
    try {
      await dismissLicenseClaimNotification(firebase, {
        orgId: currentOrgId,
        notificationId: notification.id,
      });
    } catch (err) {
      console.error('Error dismissing claim notification:', err);
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
    }
  };

  const handleDismissAll = async () => {
    if (!currentOrgId || !firebase) return;
    setDismissingAll(true);
    try {
      await Promise.all(
        notifications.map((n) =>
          dismissLicenseClaimNotification(firebase, {
            orgId: currentOrgId,
            notificationId: n.id,
          }),
        ),
      );
    } catch (err) {
      console.error('Error dismissing all claim notifications:', err);
    } finally {
      setDismissingAll(false);
    }
  };

  const handleClose = () => setIsOpen(false);

  useEffect(() => {
    if (isOpen && notifications.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [notifications.length, isOpen, loading, setIsOpen]);

  if (!isOpen || notifications.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      style={{ color: '#ffffff', maxWidth: '600px', margin: '1.75rem auto' }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Licenses Claimed ({notifications.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              The following licenses you issued have been claimed and are now active.
            </p>
            <div className="claimed-licenses-list">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    marginBottom: '0.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        marginBottom: '0.25rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {n.trackName || 'Unknown Track'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                      Claimed by: <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{n.clientOrgName || 'Unknown organization'}</span>
                      {' | '}
                      <Badge color="info" style={{ fontSize: '0.7rem' }}>{formatLicenseType(n.licenseType)}</Badge>
                      {' | '}
                      {moment(n.claimedAt).format('DD/MM/YYYY HH:mm')}
                      {n.wasShareLink && (
                        <> | <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>via share link</span></>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="btn-secondary-custom ml-3"
                    onClick={() => handleDismiss(n)}
                    disabled={dismissingIds.has(n.id) || dismissingAll}
                    style={{ minWidth: '80px' }}
                  >
                    {dismissingIds.has(n.id) ? <Spinner size="sm" /> : 'Dismiss'}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button className="btn-secondary-custom" onClick={handleClose} disabled={dismissingAll}>
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleDismissAll}
          disabled={dismissingAll || notifications.length === 0}
        >
          {dismissingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Dismissing...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>Dismiss All ({notifications.length})</span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default LicenseClaimedModal;
